import React, { forwardRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import SFDrawer from "../../drawer";
import SFCheckbox from "../../checkbox";
import SFTypography from "../../typography";
import { SFStack } from "../../grid";
import SFSearchField from "../../textField/search";
import getStyled from "../../../hooks/getStyled";

const { styled } = getStyled();

const CheckboxContainer = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginLeft: theme.spacing(-1.5), // to align the checkbox with the search field
}));

const SFSearchFieldStyled = styled(SFSearchField)(({ theme }) => ({
  "& .MuiInputBase-root": {
    "& .MuiInputBase-input": {
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(2),
      "&::placeholder": { color: theme.palette.grey400.main, opacity: 1 },
      borderRadius: "4px",
    },
    marginBottom: theme.spacing(1.25),
  },
}));

const SFDataGridSettingsDrawer = forwardRef(({ open, onClose }, ref) => {
  const { t } = useTranslation();

  // Stores the minimal information about the columns that is needed to render the content of the drawer
  const [columnInfo, setColumnInfo] = useState([]);
  // Stores the value of the search input field
  const [searchInputValue, setSearchInputValue] = useState("");

  useEffect(() => {
    if (open) {
      const columnDefs = ref.current.api.getColumnDefs();
      setColumnInfo(toColumnInfo(columnDefs));
    }
  }, [open]);

  const handleSearchInputValueChange = (value) => {
    setSearchInputValue(value);
  };

  const handleCheckboxChange = (colId) => {
    const savedState = ref.current.api.getColumnState();

    const updatedColumnDefs = savedState.map((colDef) => {
      if (colDef.colId === colId) {
        colDef.hide = !colDef.hide;
      }
      return colDef;
    });
    ref.current.api.applyColumnState({ state: updatedColumnDefs });
    setColumnInfo(toColumnInfo(ref.current.api.getColumnDefs()));
  };

  // Filter the columns based on the search input value
  const filteredColumnInfo =
    searchInputValue === ""
      ? columnInfo
      : columnInfo.filter((c) =>
        c.headerName.toLowerCase().includes(searchInputValue.toLowerCase())
      );

  return (
    <SFDrawer
      open={open}
      onClose={onClose}
      size="s"
      header={t("components.datagrid.settingsDrawer.header")}
    >
      <SFSearchFieldStyled
        variant="filled"
        placeholder={t("components.datagrid.settingsDrawer.searchPlaceholder")}
        onChange={handleSearchInputValueChange}
        initialValue={searchInputValue}
      />
      {columnInfo.length > 0 &&
        filteredColumnInfo.map((c) => {
          return (
            <SFStack key={c.colId} direction="row" alignItems="center">
              <CheckboxContainer>
                <SFCheckbox
                  checked={!c.hide}
                  onChange={() => handleCheckboxChange(c.colId)}
                />
              </CheckboxContainer>
              <SFTypography variant="formElementText">
                {c.headerName}
              </SFTypography>
            </SFStack>
          );
        })}
    </SFDrawer>
  );
});

export default SFDataGridSettingsDrawer;

/**
 * This function will map the `columnDefs` of the grid that are not suppressed (i.e. `suppressColumnsToolPanel` is not set to `true`)
 * to an array of objects with the following structure:
 * ```
 * [
 *  {
 *    headerName: string,
 *    colId: string,
 *    hide: boolean
 *  },
 *  ...
 * ]
 * ```
 * @param {Array} columnDefs The column definitions of the grid.
 * @returns {Array} An array of objects with the structure mentioned above.
 */
const toColumnInfo = (columnDefs = []) => {
  if (!Array.isArray(columnDefs)) {
    return [];
  }
  return columnDefs
    .filter((column) => column.suppressColumnsToolPanel !== true)
    .map((column) => ({
      headerName: column.headerName,
      colId: column.colId,
      hide: column.hide,
    }));
};
